import { useCallback } from 'react';

import { getWindowSafe } from '@change-corgi/core/window';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { copyToClipboard } from 'src/app/shared/utils/clipboard';

import { useShareUrlWithUtmParams } from '../../utils/getShareUrlWithUtmParams';
import { replaceUrlInMessage } from '../../utils/replaceUrlInMessage';
import { useAutoCheckOffTask } from '../sharedHooks';

import type { ActionsReturnType, GroupSharingHookProps } from './types';

const TRACKING_EVENT_NAME = 'share_petition';

export function useFacebookGroupPostSharing(props: GroupSharingHookProps): ActionsReturnType {
	const i18n = useI18n();
	const autoCheckOffTask = useAutoCheckOffTask();

	const {
		message,
		shareUrl = '',
		trackingProps: { context, viewContext },
		recommendedGroup,
	} = props;

	const shareUrlWithUtmParams = useShareUrlWithUtmParams(shareUrl, 'facebook_group');
	const track = useTracking();
	const window = getWindowSafe();

	const primaryAction = useCallback(async () => {
		const messageToCopy = replaceUrlInMessage(message, shareUrl, shareUrlWithUtmParams || shareUrl);
		void copyToClipboard(messageToCopy, i18n);

		const trackingData = {
			context,
			location: 'facebook_group',
			tab_at_share: viewContext,
			content: 'post',
			app_available: false,
		};
		void track(TRACKING_EVENT_NAME, trackingData);

		if (window) {
			window.open(recommendedGroup?.url, '_blank', 'noopener,noreferrer');
		}
		await autoCheckOffTask('share_communities');
	}, [
		message,
		shareUrl,
		shareUrlWithUtmParams,
		i18n,
		context,
		viewContext,
		track,
		window,
		autoCheckOffTask,
		recommendedGroup?.url,
	]);

	const secondaryAction = useCallback(() => {
		const trackingData = {
			context,
			location: 'facebook_group',
			tab_at_share: viewContext,
			app_available: false,
			content: 'view_group',
		};

		void track(TRACKING_EVENT_NAME, trackingData);
		if (window) {
			window.open(recommendedGroup?.url, '_blank', 'noopener,noreferrer');
		}
	}, [context, recommendedGroup?.url, track, viewContext, window]);

	return {
		primaryAction,
		secondaryAction,
	};
}
